import React from "react";

/**
 * Placeholder rows shown while a pane's data is in flight.
 *
 * These exist because the alternative was worse than blank: every list on this
 * page rendered its EMPTY state during loading, so a pane mid-fetch actively
 * asserted "No service breakdown for this account and period" — a claim about
 * the data that had not been checked yet. On a cost dashboard that is a
 * correctness problem, not a polish one.
 *
 * The handoff argued against per-section spinners on top of the global loading
 * bar, and that reasoning still holds for a *spinner*: a period change would
 * fill the page with them. Skeletons are quieter — they hold the pane's shape
 * so nothing reflows when the real rows arrive, and they say "not yet" instead
 * of "nothing".
 */
export default function SkeletonRows({ count = 3, lines = 2, compact = false }) {
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: count }).map((_, row) => (
        <div className={`skeleton-row${compact ? " skeleton-row-compact" : ""}`} key={row} aria-hidden="true">
          {Array.from({ length: lines }).map((__, line) => (
            <span
              className={`skeleton-line${line === 0 ? " skeleton-line-title" : " skeleton-line-sub"}`}
              key={line}
              /* Varying widths so a column of placeholders reads as text
                 waiting to arrive rather than as a loading graphic. */
              style={{ width: `${line === 0 ? 92 - row * 9 : 58 - row * 6}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** The stat row's own shape, for the initial load before any figure exists. */
export function SkeletonStats({ count = 4 }) {
  return (
    <div className="stat-row" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading figures…</span>
      {Array.from({ length: count }).map((_, i) => (
        <div className="stat-cell" key={i} aria-hidden="true">
          <span className="skeleton-line skeleton-line-kicker" />
          <span className="skeleton-line skeleton-line-stat" />
          <span className="skeleton-line skeleton-line-sub" />
        </div>
      ))}
    </div>
  );
}
